import React, { useRef, useEffect, useState } from 'react';
import Modal from '@/components/common/Modal';
import SubmitFooter from '@/components/ui/Modal/SubmitFooter';
import Input from '@/components/ui/Form/Input';
import { useForm } from 'react-hook-form';
import { twMerge } from '@/utils';

export type IStaffer = {
  email: string;
  firstName: string;
  lastName: string;
};

interface FeedbackModalProps {
  close: () => void;
  currentPageUrl: string;
  isOpen: boolean;
  staffer: IStaffer;
}

const ACTION_URL = 'https://webto.salesforce.com/servlet/servlet.WebToCase?encoding=UTF-8&orgId=00Di0000000J5oJ';
const HIDDEN_IFRAME_NAME = 'JLA_API';

const FeedbackModal = ({
  close,
  currentPageUrl,
  isOpen,
  staffer: { email, firstName, lastName },
}: FeedbackModalProps) => {
  const { register, watch, reset } = useForm();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const thanksRef = useRef<HTMLSpanElement>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const descriptionValue = watch('description');
  const subjectValue = watch('subject');

  const closeModal = () => {
    if (submitting || submitted) reset({ description: '', subject: '' });
    close();
  };

  useEffect(() => {
    setSubmitted(false);
    setSubmitting(false);
  }, [isOpen]);

  // The form unmounts on submit, dropping focus to <body>; keep it inside the dialog.
  useEffect(() => {
    if (submitted) thanksRef.current?.focus();
  }, [submitted]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (iframe) {
      const handleLoad = () => {
        if (isOpen && submitting) {
          setSubmitting(false);
          setSubmitted(true);
        }
      };
      iframe.addEventListener('load', handleLoad);
      return () => iframe.removeEventListener('load', handleLoad);
    }
  }, [isOpen, submitting]);

  return (
    <>
      <Modal
        reflow
        closeModal={closeModal}
        headerText="Share Feedback"
        id="feedback-modal"
        isOpen={isOpen}
      >
        {submitted && (
          <span ref={thanksRef} className="tw-text-base" tabIndex={-1}>Thank you for sharing your feedback with us.</span>
        )}
        {!submitted && (
          <form
            action={ACTION_URL}
            autoComplete="off"
            className="tw-text-base"
            method="POST"
            target={HIDDEN_IFRAME_NAME}
            onSubmit={(e) => {
              if (submitting) {
                e.preventDefault();
                return;
              }

              const form = e.currentTarget as HTMLFormElement;
              const descriptionField = form.elements.namedItem('description') as HTMLTextAreaElement;

              if (descriptionField) {
                descriptionField.value += `\n[URL: ${currentPageUrl}]`;
              }

              setSubmitting(true);
            }}
          >
            <input name="orgid" type="hidden" value="00Di0000000J5oJ" />
            <input name="retURL" type="hidden" value="http://" />
            <input maxLength={80} name="name" type="hidden" value={`${firstName} ${lastName}`} />
            <input maxLength={20} name="00N6S00000OOjxL" type="hidden" value={firstName} />
            <input maxLength={20} name="00N6S00000OOjxN" type="hidden" value={lastName} />
            <input maxLength={80} name="email" type="hidden" value={email} />
            <input name="00N0H00000It7ue" type="hidden" value="User Experience" />
            <input name="00Ni000000Hal9w" type="hidden" value="Tier 2" />
            <input name="origin" type="hidden" value="In-App Feedback" />
            <Input
              className="!tw-max-w-full"
              id="feedback-subject"
              label="Subject"
              maxLength={80}
              name="subject"
              type="text"
              {...register('subject')}
            />
            <div className="tw-flex tw-gap-2 tw-flex-col">
              <label
                className="tw-pt-2 tw-mt-2"
                htmlFor="feedback-description"
              >
                What would you like to tell us?
              </label>
              <textarea
                className={twMerge('tw-w-full tw-border-gray-300 tw-border tw-border-solid tw-rounded tw-p-2.5',
                  'tw-box-border tw-font-tci tw-min-h-[100px]')}
                id="feedback-description"
                name="description"
                {...register('description')}
              />
              <SubmitFooter
                close={closeModal}
                isLoading={submitting}
                isValid={subjectValue && descriptionValue}
              />
            </div>
          </form>
        )}
      </Modal>
      <iframe
        ref={iframeRef}
        className="tw-hidden"
        id={HIDDEN_IFRAME_NAME}
        name={HIDDEN_IFRAME_NAME}
        src="about:blank"
        title={HIDDEN_IFRAME_NAME}
      />
    </>
  );
};

export default FeedbackModal;
